import { useRef, useEffect, useState, type FunctionComponent } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import cx from 'classnames';
import { getWindow } from '@surfline/web-common';

import CloudflareResizedImage from 'components/CloudflareResizedImage';
import { ChevronDown } from 'components/Icons/ChevronDown';
import { useMinWidthDesktopSmall } from 'hooks/useMediaQueries';
import type { HeroSectionType } from 'types/contentful/content-types';

import Styles from './TravelHero.module.scss';

export interface TravelHeroProps {
  data: HeroSectionType;
}

export const TravelHero: FunctionComponent<TravelHeroProps> = ({ data }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const isMinWidthDesktopSmall = useMinWidthDesktopSmall();
  const heroVideoSrc = isMinWidthDesktopSmall
    ? data?.heroVideoDesktop?.url
    : data?.heroVideoMobile?.url;
  const heroVideoType = isMinWidthDesktopSmall
    ? data?.heroVideoDesktop?.contentType
    : data?.heroVideoMobile?.contentType;

  // Set mounted state to trigger re-render and show video source
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Load video after source is dynamically added post-mount
  // The autoPlay attribute will handle play attempt after load completes
  useEffect(() => {
    if (!data?.isVideoHero || !isMounted || !videoRef.current || !heroVideoSrc) {
      return;
    }

    const video = videoRef.current;
    // Load the video to re-evaluate the dynamically added source
    // The autoPlay attribute should trigger play once loading is complete
    video.load();
  }, [data?.isVideoHero, isMounted, heroVideoSrc]);

  const handleClick = (id: string) => {
    const win = getWindow();
    const element = win?.document?.getElementById(id);
    const headerOffset = 45;
    const elementPosition = element?.getBoundingClientRect()?.top ?? 0;
    const offsetPosition = elementPosition + win?.pageYOffset - headerOffset;
    win?.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  return (
    <section
      data-testid="travel-hero"
      className={cx(Styles.travelHero, { [Styles.travelHeroCenter]: data?.centerTextVertically })}
    >
      <div className={Styles.mediaWrapper}>
        {data?.isVideoHero ? (
          <video
            ref={videoRef}
            autoPlay
            className={Styles.media}
            data-testid="travel-hero-video"
            key={heroVideoSrc}
            loop
            muted
            playsInline
            poster={data?.heroPoster?.url}
          >
            {isMounted && <source src={heroVideoSrc} type={heroVideoType} />}
          </video>
        ) : (
          data?.heroPoster && (
            <CloudflareResizedImage
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              alt={data?.heroPoster?.fileName}
              src={data.heroPoster.url}
              data-testid="travel-hero-image"
            />
          )
        )}
      </div>
      <div className={Styles.textWrapper}>
        {data?.title && (
          <Typography
            variant="headline"
            component="h1"
            className={Styles.title}
            data-testid="travel-hero-title"
            id="travel-hero-title"
          >
            {data.title}
          </Typography>
        )}
        {data?.subtitle && (
          <Typography
            variant="headline"
            component="h2"
            className={Styles.subtitle}
            data-testid="travel-hero-subtitle"
          >
            {data.subtitle}
          </Typography>
        )}
        {data?.showScrollArrow && (
          <Box className={Styles.scrollArrow}>
            <IconButton
              color="primary"
              aria-label="scroll to next section"
              component="button"
              data-testid="travel-hero-arrow"
              onClick={() => handleClick('the-waves')}
              className={Styles.scrollIcon}
            >
              <ChevronDown />
            </IconButton>
          </Box>
        )}
      </div>
    </section>
  );
};

export default TravelHero;
